import { buildEverflowDomains } from '@pickleballinc/lib/everflow/url';

import { publicEnv } from '@/lib/env';
import { ViewOption } from '@/lib/types';
import { Options } from '@/utils/types';

export const BLOG_LABEL = 'Blog';
export const UNCATEGORIZED_LABEL = 'Uncategorized';

export const IS_PRODUCTION = publicEnv.NEXT_PUBLIC_APP_ENV === 'prod';
export const IS_DEVELOPMENT = publicEnv.NEXT_PUBLIC_APP_ENV === 'dev';
export const RTE_ENVIRONMENT = IS_PRODUCTION ? 'production' : IS_DEVELOPMENT ? 'development' : 'uat';
export const IS_TEST = publicEnv.NEXT_PUBLIC_APP_ENV === 'test';
export const IS_LOCALHOST = publicEnv.NEXT_PUBLIC_PTL_URL.includes('localhost');
/**
 * Local-testing override for the impression tracking flow. Flip to `true` IN THIS FILE when you want to
 * exercise the full FE → route → BE path on localhost — the provider's localhost guard and the
 * addImpression route's localhost mock + origin allowlist all bypass. Must stay `false` on any commit to
 * dev/test/prod.
 */
export const IMPRESSIONS_FORCE_ENABLED = false;

export const PICKLEBALL_PLACEHOLDER_IMAGE = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/PB-gray.svg`;
export const CLUBS_PLACEHOLDER_IMAGE = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/PC-gray.svg`;
export const LEAGUES_PLACEHOLDER_IMAGE = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/PL-gray.svg`;
export const TOURNAMENT_PLACEHOLDER_IMAGE = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/PT-gray.svg`;
export const TEAM_LEAGUES_PLACEHOLDER_IMAGE = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/PTL-gray.svg`;
export const WPR_PLACEHOLDER_IMAGE = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/APR-gray.svg`;
export const PICKLEBALL_CENTRAL_LOGO = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/outside/PICKLEBALL_CENTRAL_LOGO.svg`;

export const viewOptions: ViewOption[] = [
	{
		value: 'Schedule',
		label: 'Schedule'
	},
	{
		value: 'Week',
		label: 'Week'
	}
] as const;

export const MATCH_ACTIONS = [
	{
		value: '2',
		label: 'Home team-WD'
	},
	{
		value: '3',
		label: 'Away team-WD'
	},
	{
		value: '4',
		label: 'Cancel'
	}
] as const;

export const MATCH_MODE = {
	Lineup: 'Lineup',
	Scores: 'Scores',
	Final: 'Final'
} as const;

export const MATCH_RETIRE_ACTIONS = {
	RETIRE: 'Retire',
	FORFEIT: 'Forfeit',
	UNDO_RETIRE: 'Undo Retire',
	UNDO_FORFEIT: 'Undo Forfeit'
} as const;

export const MATCH_RETIRE_OPTIONS: ViewOption[] = [
	{
		value: MATCH_RETIRE_ACTIONS.RETIRE,
		label: MATCH_RETIRE_ACTIONS.RETIRE
	},
	{
		value: MATCH_RETIRE_ACTIONS.FORFEIT,
		label: MATCH_RETIRE_ACTIONS.FORFEIT
	}
] as const;

export const GAME_NUMBERS = {
	G1: 'G1',
	G2: 'G2',
	G3: 'G3',
	G4: 'G4',
	G5: 'G5',
	FINAL: 'Final'
} as const;

export const GAME_NUMBER_TO_INDEX: Record<string, number> = {
	[GAME_NUMBERS.G1]: 0,
	[GAME_NUMBERS.G2]: 1,
	[GAME_NUMBERS.G3]: 2,
	[GAME_NUMBERS.G4]: 3,
	[GAME_NUMBERS.G5]: 4,
	[GAME_NUMBERS.FINAL]: 5
} as const;

export const GAME_INPUTS = [
	{
		gameNumber: GAME_NUMBERS.G1
	},
	{
		gameNumber: GAME_NUMBERS.G2
	},
	{
		gameNumber: GAME_NUMBERS.G3
	},
	{
		gameNumber: GAME_NUMBERS.G4
	},
	{
		gameNumber: GAME_NUMBERS.G5
	},
	{
		gameNumber: GAME_NUMBERS.FINAL
	}
] as const;

export const MATCHUP_MODAL_TYPE = {
	WITHDRAW: 'withdraw',
	CANCEL: 'cancel'
} as const;

export const subStepContent: Record<number, { title: string; description: string }> = {
	1: {
		title: 'Player to be subbed out',
		description: 'Please select the player who is leaving the game.'
	},
	2: {
		title: 'Player to be subbed in',
		description: 'Please search and select the player who is entering the game as a replacement.'
	},
	3: {
		title: 'Select Matches & Games',
		description: 'Select all the matches and games you are subbing the player.'
	},
	4: {
		title: 'Confirm substitution',
		description: 'Please check if everything seems correct.'
	}
};

export const subSidebarTitleAndSubtitle: Record<number, { title: string; description: string }> = {
	1: {
		title: 'Players from your team',
		description: 'Select the player who is entering the game as a replacement.'
	},
	2: {
		title: 'Players from sub list',
		description: 'Select the player who is entering the game as a replacement.'
	},
	3: {
		title: 'Search all players in Pickleball.com',
		description: 'Please search and select the player who is entering the game as a replacement.'
	}
};

export enum PlayerFormatType {
	PLAYER_STANDINGS_FORMAT_TYPE_UNDEFINED = 0,
	PLAYER_STANDINGS_TYPE_MENS_SINGLES = 1,
	PLAYER_STANDINGS_TYPE_WOMENS_SINGLES = 2,
	PLAYER_STANDINGS_TYPE_MENS_DOUBLES = 3,
	PLAYER_STANDINGS_TYPE_WOMENS_DOUBLES = 4,
	PLAYER_STANDINGS_TYPE_MIXED_DOUBLES = 5,
	PLAYER_STANDINGS_TYPE_MENS_SKINNY = 6,
	PLAYER_STANDINGS_TYPE_WOMENS_SKINNY = 7,
	PLAYER_STANDINGS_TYPE_OVERALL = 8
}

export const sortOptions: Options[] = [
	{
		value: 'standing',
		label: 'Rank'
	},
	{
		value: 'first_name',
		label: 'By name'
	},
	{
		value: 'reg_wins',
		label: 'Match - Win'
	},
	{
		value: 'reg_losses',
		label: 'Match - Losses'
	},
	{
		value: 'matches_win_pct',
		label: 'Match - Win %'
	},
	{
		value: 'game_wins',
		label: 'Games - Win'
	},
	{
		value: 'game_losses',
		label: 'Games - Losses'
	},
	{
		value: 'game_win_pct',
		label: 'Games - Win %'
	},
	{
		value: 'points_earned',
		label: 'Points - Earned'
	},
	{
		value: 'points_against',
		label: 'Points - Against'
	},
	{
		value: 'points_diff_pct',
		label: 'Points - Diff %'
	},
	{
		value: 'tb_points_earned',
		label: 'TB Points - Earned'
	},
	{
		value: 'tb_points_against',
		label: 'TB Points - Against'
	},
	{
		value: 'tb_points_diff_pct',
		label: 'TB Points - Diff %'
	},
	{
		value: 'avg_partner_rating',
		label: 'Ratings - Partner'
	},
	{
		value: 'avg_opponent_rating',
		label: 'Ratings - Opponent'
	},
	{
		value: 'adjusted_rating',
		label: 'Ratings - Adjusted'
	},
	{
		value: 'total_rating',
		label: 'Ratings - Overall'
	}
];

export const DEFAULT_PAGE_SIZE = 25;

export const GAME_SCORE_MAX = 99;

export const WHITE_LISTED_PAGES = ['', '/', '/team-leagues'];

/**
 * Pages that must render no Freestar scripts, slots or products at all.
 *
 * Matched with `isAdFreePath` from `@pickleballinc/ui/lib/freestar-ad-free-paths`:
 * `*` is exactly one segment, `**` is the rest of the path.
 */
export const AD_FREE_PAGES = [
	// The matchup page plus the captain-only lineup / substitution / tie-breaker pages under it.
	'/team-leagues/*/matchup/**',
	// Game day. The trailing `*` is required: `/gameday` on its own is the spectator index, which
	// carries its own banner and gallery ads and stays monetised.
	'/team-leagues/*/gameday/*/**',
	// Captain pages.
	'/team-leagues/*/team-info/**',
	'/team-leagues/*/teams/*/player-moves/**',
	'/team-leagues/*/team-details/*/manage/**'
];
export const FRESHDESK_CHAT_BOT_TOKEN = 'd0a37f09-4ea6-4fdd-b698-e44d1308f2b6';
export const FRESHDESK_CHAT_BOT_HOST = 'https://pickleball.freshchat.com';
export const FRESHDESK_SCRIPT_ID = 'Freshchat-js-sdk';
export const FRESHDESK_SCRIPT_SRC = 'https://pickleball.freshchat.com/js/widget.js';
export const FRESHDESK_WIDGET_UUID = '728b8f02-0c26-4d66-bca9-ee2bb6b4fc01';

/**
 * Registrable domains the Everflow link decorator treats as ours (apex URLs only — every subdomain such as
 * checkout.*, sso.*, brackets.* matches by suffix). See docs/everflow-tracking.md.
 */
export const EVERFLOW_DOMAINS = buildEverflowDomains([
	publicEnv.NEXT_PUBLIC_HOME_URL,
	publicEnv.NEXT_PUBLIC_PT_URL,
	publicEnv.NEXT_PUBLIC_PL_URL,
	publicEnv.NEXT_PUBLIC_PC_URL,
	publicEnv.NEXT_PUBLIC_PTL_URL,
	publicEnv.NEXT_PUBLIC_APR_URL,
	publicEnv.NEXT_PUBLIC_PBRACKETS_URL
]);
